/**
 * 모바일 디자인 메트릭스
 * 
 * 이 파일은 현재 프로젝트의 모바일 디자인 요소들을 정확히 추출하여 문서화합니다.
 * 데스크톱 전환 시 이 값들을 그대로 유지하여 시각적 일관성을 보장합니다.
 * 
 * @created 2025-07-29
 */

// ============================================
// 1. 기본 단위 및 브레이크포인트
// ============================================
export const BREAKPOINTS = {
  mobile: 768,     // 모바일/데스크톱 경계선
  tablet: 1024,    // 태블릿 경계선 (참고용)
  desktop: 1280    // 데스크톱 경계선 (참고용)
};

// ============================================
// 2. 레이아웃 구조
// ============================================
export const LAYOUT = {
  header: {
    height: 50,                    // 헤더 높이 (px)
    backgroundColor: '#FFFFFF',    // 모바일 헤더 배경색
    textColor: '#231F20'          // 모바일 헤더 텍스트색
  },
  mobileNav: {
    height: 68,                    // 하단 네비게이션 높이 (Safe Area 제외)
    backgroundColor: '#FFFFFF',    // 네비게이션 배경색
    borderTop: '1px solid #e5e7eb', // 상단 보더
    iconSize: 20,                  // 아이콘 크기
    buttonHeight: 30,              // 버튼 높이
    buttonPadding: 0,              // 버튼 패딩
    gap: 0                         // 버튼 간 간격
  },
  safeArea: {
    // iOS Safe Area는 동적으로 계산
    dynamicBottom: 'env(safe-area-inset-bottom, 0px)',
    dynamicTop: 'env(safe-area-inset-top, 0px)'
  }
};

// ============================================
// 3. 여백 시스템 (Spacing)
// ============================================
export const SPACING = {
  // 페이지 레벨 패딩
  page: {
    mobile: 16,       // 1rem - 모든 페이지 기본 패딩
    // CSS 클래스: p-4
  },
  
  // 컨테이너/카드 내부 패딩
  container: {
    xs: 8,            // 0.5rem - p-2
    sm: 12,           // 0.75rem - p-3
    default: 16,      // 1rem - p-4
    lg: 24,           // 1.5rem - p-6
  },
  
  // 요소 간 간격
  gap: {
    xs: 4,            // 0.25rem - gap-1
    sm: 8,            // 0.5rem - gap-2, space-y-2
    default: 16,      // 1rem - gap-4, space-y-4
    lg: 24,           // 1.5rem - gap-6
  },
  
  // 섹션별 특수 여백
  section: {
    title: 12,        // 0.75rem - 섹션 제목 하단 (mb-3)
    headerBottom: 16, // 1rem - 헤더와 콘텐츠 간격
  }
};

// ============================================
// 4. 컴포넌트 스타일
// ============================================
export const COMPONENTS = {
  // 카드 컴포넌트
  card: {
    borderRadius: '1.5rem',        // rounded-3xl (24px)
    shadow: 'shadow-lg',           // 기본 그림자
    backgroundColor: '#FFFFFF',     // bg-white
    padding: {
      sm: 12,                      // p-3
      default: 16,                 // p-4
      lg: 24                       // p-6
    }
  },
  
  // 버튼 컴포넌트
  button: {
    height: {
      xs: 32,                      // 작은 버튼
      sm: 36,                      // 작은 버튼
      default: 40,                 // 기본 버튼
      lg: 48                       // 큰 버튼
    },
    padding: {
      xs: '4px 8px',              // px-2 py-1
      sm: '6px 12px',             // px-3 py-1.5
      default: '8px 16px',        // px-4 py-2
      lg: '12px 24px'             // px-6 py-3
    },
    borderRadius: {
      default: '0.25rem',         // rounded (4px)
      lg: '0.5rem',               // rounded-lg (8px)
      full: '9999px'              // rounded-full
    },
    fontSize: {
      xs: '0.75rem',              // text-xs
      sm: '0.875rem',             // text-sm
      default: '0.875rem',        // text-sm
      lg: '1rem'                  // text-base
    }
  },
  
  // 모달 컴포넌트
  modal: {
    width: '95vw',                 // 모바일 모달 너비
    maxHeight: 'calc((100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) * 0.75)',
    borderRadius: '1.5rem',        // rounded-3xl
    padding: 16,                   // p-4
    headerPadding: 16,             // 헤더 패딩
    zIndex: 9999980                // 모달 z-index
  },
  
  // 리스트 아이템
  listItem: {
    padding: 10,                   // p-2.5 (정확히 10px)
    borderRadius: '0.5rem',        // rounded-lg
    gap: 8,                        // 아이템 간 간격 (space-y-2)
    border: '1px solid #e5e7eb',   // border-gray-200
    selectedBorder: '2px solid #4ABC85' // 선택된 아이템
  }
};

// ============================================
// 5. 타이포그래피
// ============================================
export const TYPOGRAPHY = {
  // 폰트 크기 (모바일에서 90% 적용됨)
  fontSize: {
    xs: '0.625rem',     // 10px (기본 0.75rem의 90%)
    sm: '0.75rem',      // 12px (기본 0.875rem의 90%)
    base: '0.8rem',     // 12.8px (기본 1rem의 90%)
    lg: '0.9rem',       // 14.4px (기본 1.125rem의 90%)
    xl: '1rem',         // 16px (기본 1.25rem의 90%)
    '2xl': '1.1rem',    // 17.6px
    '3xl': '1.2rem'     // 19.2px
  },
  
  // 특수 컴포넌트 폰트
  component: {
    cardTitle: '0.875rem',        // text-[14px] - 카드 제목
    sectionTitle: '0.875rem',     // 섹션 제목
    tableText: '0.7rem',          // 테이블 텍스트
    chartText: '0.6rem',          // 차트 레이블
    buttonText: '0.75rem'         // 버튼 텍스트
  },
  
  // 폰트 굵기
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700
  }
};

// ============================================
// 6. 색상 시스템
// ============================================
export const COLORS = {
  // Primary 색상
  primary: '#4ABC85',
  primaryHover: '#3DA575',
  
  // 배경색
  background: {
    page: '#F3F4F6',              // bg-gray-100
    card: '#FFFFFF',              // bg-white
    hover: '#F9FAFB',             // bg-gray-50
    selected: '#F0FDF4'           // bg-green-50
  },
  
  // 텍스트 색상
  text: {
    primary: '#1F2937',           // text-gray-800
    secondary: '#6B7280',         // text-gray-500
    muted: '#9CA3AF'              // text-gray-400
  },
  
  // 보더 색상
  border: {
    default: '#E5E7EB',           // border-gray-200
    light: '#F3F4F6',             // border-gray-100
    dark: '#D1D5DB'               // border-gray-300
  }
};

// ============================================
// 7. 애니메이션 및 트랜지션
// ============================================
export const ANIMATION = {
  // 트랜지션 설정
  transition: {
    fast: '150ms',
    default: '200ms',
    slow: '300ms'
  },
  
  // 이징 함수
  easing: {
    default: 'ease-in-out',
    easeIn: 'ease-in',
    easeOut: 'ease-out'
  }
};

// ============================================
// 8. 특수 페이지별 메트릭스
// ============================================
export const PAGE_METRICS = {
  // 학습 분석 페이지
  learningStatus: {
    area1: {
      padding: 12,                // p-3
      itemGap: 8                  // space-y-2
    },
    area2: {
      padding: 12,                // p-3
      headerPadding: 16           // p-4
    },
    area3: {
      padding: 12,                // p-3
      headerPadding: 16           // p-4
    }
  },
  
  // 시험 관리 페이지
  schoolExams: {
    cardGrid: {
      gap: 16,                    // gap-4
      columns: 1                  // 모바일에서 1열
    }
  }
};

// ============================================
// 9. Z-Index 계층
// ============================================
export const Z_INDEX = {
  mobileNav: 9999999,             // 최상위 - 모바일 네비게이션
  modal: 9999980,                 // 모달
  dropdown: 9999970,              // 드롭다운
  tooltip: 9999960,               // 툴팁
  header: 9999                    // 헤더
};

// ============================================
// 10. 유틸리티 함수
// ============================================

/**
 * 픽셀을 rem으로 변환
 */
export const pxToRem = (px) => `${px / 16}rem`;

/**
 * 모바일 메트릭스를 CSS 변수로 변환
 */
export const toCSSVariables = () => {
  const variables = {};
  
  // Spacing 변수
  Object.entries(SPACING).forEach(([category, values]) => {
    if (typeof values === 'object') {
      Object.entries(values).forEach(([key, value]) => {
        variables[`--mobile-${category}-${key}`] = `${value}px`;
      });
    }
  });
  
  // Typography 변수
  Object.entries(TYPOGRAPHY.fontSize).forEach(([key, value]) => {
    variables[`--mobile-font-${key}`] = value;
  });
  
  return variables;
};

// ============================================
// 11. Tailwind 클래스 매핑
// ============================================
export const TAILWIND_CLASSES = {
  // 패딩 클래스
  padding: {
    8: 'p-2',
    12: 'p-3',
    16: 'p-4',
    24: 'p-6',
    32: 'p-8'
  },
  
  // 간격 클래스
  gap: {
    4: 'gap-1 space-y-1',
    8: 'gap-2 space-y-2',
    16: 'gap-4 space-y-4',
    24: 'gap-6 space-y-6'
  },
  
  // 테두리 반경
  borderRadius: {
    '0.25rem': 'rounded',
    '0.5rem': 'rounded-lg',
    '1.5rem': 'rounded-3xl',
    '9999px': 'rounded-full'
  }
};

// 기본 export
export default {
  BREAKPOINTS,
  LAYOUT,
  SPACING,
  COMPONENTS,
  TYPOGRAPHY,
  COLORS,
  ANIMATION,
  PAGE_METRICS,
  Z_INDEX,
  TAILWIND_CLASSES,
  pxToRem,
  toCSSVariables
};